import { gql } from '@apollo/client';
import * as React from 'react';

import { WorkItemStatus, WorkItemUserCategory } from '../../../__generated__/globalTypes';
import { useAlertBlockedNotice } from '../../../hooks/assistant/useAlertBlockedNotice';
import { useOpenAlertInAssistant } from '../../../hooks/assistant/useOpenAlertInAssistant';
import { useMarkWorkItemsRead } from '../../../hooks/mutations/useMarkWorkItemsRead';
import { useAssistantTurnStatus } from '../../../providers/AssistantTurnStatusProvider';
import { isWorkItemUnread } from '../../../utils/alert/workItemAlertRow';

import RecordAlertBannerBase from './RecordAlertBannerBase';

import type { RecordAlertBannerFragment } from './__generated__/RecordAlertBanner.gql';
import type { RecordAlertBannerTone } from './RecordAlertBannerBase';

/** The fields the banner reads off a record's work item. Pages spread it into their own read. */
const RecordAlertBannerFragment = gql`
  fragment RecordAlertBanner on WorkItem {
    id
    title
    category
    status
    read
  }
`;

/**
 * A new `WorkItemUserCategory` does not compile until it picks a tint. The
 * palette follows `AlertTypeIcon`'s severity ladder rather than its hues:
 * an update is a flagged anomaly, a reminder is a nudge, a config item asks
 * the user to fix something before the product works.
 */
export const TONE_BY_WORK_ITEM_CATEGORY: Record<WorkItemUserCategory, RecordAlertBannerTone> = {
  [WorkItemUserCategory.Update]: 'warning',
  [WorkItemUserCategory.Reminder]: 'blue-light',
  [WorkItemUserCategory.Config]: 'error',
};

interface IRecordAlertBannerProps {
  /** The record's work item, as the read returns it — `null` where the record has none. */
  workItem: RecordAlertBannerFragment | null | undefined;
  className?: string;
}

/**
 * The record page's active-alert banner: the record's work item rendered as
 * its title, shown only while the item is `ACTIVE`. A record read returns any
 * unarchived item, and an item assigned to another household member or not
 * yet due is `PENDING` — that one is the assistant's to surface, not the page's.
 *
 * A click marks the item read and expands it into the assistant thread the
 * way an Alerts Center row does, including the mid-turn refusal: while the
 * assistant is working a turn the click is refused whole, so the item is not
 * marked read either. Archiving is not offered here — it
 * happens in the thread.
 */
const RecordAlertBanner = ({ workItem, className }: IRecordAlertBannerProps) => {
  const openAlertInAssistant = useOpenAlertInAssistant();
  const { markRead } = useMarkWorkItemsRead();
  const { isTurnInFlight } = useAssistantTurnStatus();
  const { blockedNoticeAlertId, showBlockedNotice } = useAlertBlockedNotice(isTurnInFlight);

  const handleClick = React.useCallback(() => {
    if (!workItem) {
      return;
    }
    if (isTurnInFlight) {
      showBlockedNotice(workItem.id);
      return;
    }

    // Read off the item before `markRead` lands: the transcript needs to know
    // whether this was new to the user.
    const wasUnread = isWorkItemUnread(workItem);
    void markRead([workItem.id]);
    openAlertInAssistant(workItem.id, wasUnread);
  }, [workItem, isTurnInFlight, showBlockedNotice, markRead, openAlertInAssistant]);

  if (!workItem || workItem.status !== WorkItemStatus.Active) {
    return null;
  }

  return (
    <RecordAlertBannerBase
      title={workItem.title}
      tone={TONE_BY_WORK_ITEM_CATEGORY[workItem.category]}
      onClick={handleClick}
      showBlockedNotice={blockedNoticeAlertId === workItem.id}
      className={className}
    />
  );
};

export default Object.assign(RecordAlertBanner, { Fragment: RecordAlertBannerFragment });
